import * as React from 'react';

import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { Link, useParams } from 'react-router-dom';

import { useGetPayStation } from 'src/features/payStations/api/useGetPaystation';
import { useListPayStationHeartBeats } from 'src/features/payStations/api/useListPayStationHeartbeats';
import payStationsMessages from 'src/features/payStations/utils/messages';
import useFilters from 'src/hooks/useFilters';
import usePagination from 'src/hooks/usePagination';
import useSearch from 'src/hooks/useSearch';
import useSorting from 'src/hooks/useSorting';
import {
    CellDatetime,
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import { Button } from 'src/registry/ui/Button';
import { useTitleStore } from 'src/stores/useTitleStore';

import type { PayStationHeartBeat } from 'src/features/payStations/types';

const PayStationHeartBeatsTable = () => {
    const { formatMessage } = useIntl();

    const { queryFiltersOptions, ...filters } = useFilters();
    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'timestamp', desc: true }],
    });
    const { queryPaginationOptions, ...pagination } = usePagination();
    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'search' });

    const { payStationId } = useParams();

    const { data, isLoading, isError } = useListPayStationHeartBeats({
        payStationId: payStationId || '',
        config: { enabled: !!payStationId },
        ...querySortingOptions,
        ...queryPaginationOptions,
        ...queryFiltersOptions,
        ...querySearchOptions,
    });
    const { data: payStationData } = useGetPayStation({
        payStationId: payStationId || '',
        config: { enabled: !!payStationId },
    });
    const { setTitle, removeTitle } = useTitleStore();
    React.useEffect(() => {
        if (!payStationData) return;
        setTitle(payStationData.identifier);
        return () => {
            removeTitle();
        };
    }, [payStationData]);

    const columnHelper = createColumnHelper<PayStationHeartBeat>();

    const columns = [
        columnHelper.accessor('timestamp', {
            header: DataTableColumnHeader,
            cell: CellDatetime,
            enableColumnFilter: true,
            enableSorting: true,
            enableHiding: true,
            meta: {
                title: formatMessage(payStationsMessages.date),
                inputType: INPUT_TYPE.DATE,
            },
        }),
    ];

    return (
        <>
            <Button asChild className="-mt-6 place-self-start p-0" variant="link">
                <Link to="/pay-stations">
                    <FontAwesomeIcon icon={faChevronLeft} className="opacity-50" />
                    {formatMessage(payStationsMessages.backToAllPayStationDevices)}
                </Link>
            </Button>
            <DataTable
                columns={columns}
                data={data}
                pagination={pagination}
                sorting={sorting}
                filters={filters}
                search={search}
                semantics={{
                    title: formatMessage(payStationsMessages.heartBeats),
                    entryName: formatMessage(payStationsMessages.heartBeat),
                    entryNames: formatMessage(payStationsMessages.heartBeats),
                }}
                isError={isError}
                isLoading={isLoading}
                getRowId={(row) => row.id.toString()}
                components={{
                    controls: DataTableControls,
                    pagination: DataTablePagination,
                }}
                enableLocalFilters={true}
            />
        </>
    );
};

export default PayStationHeartBeatsTable;
